import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import SegmentedTabs, { SegmentedTabItem } from '../../components/SegmentedTabs';
import {
    useCancelCodeMutation,
    useDeleteCodeMutation,
    useGetVouchersQuery,
} from '../../api/voucher/voucherApiSlice';
import { useGetAdminGroupsQuery } from '../../api/group/groupAdminApiSlice';
import { useIsMaster } from '../../common/useIsMaster';
import { AdminVoucher, CreateCampaignResult, VoucherScope } from '../../api/voucher/voucherApiTypes';
import { useGetPriceAdjustmentsQuery } from '../../api/priceAdjustment/priceAdjustmentApiSlice';
import { PriceAdjustment } from '../../api/priceAdjustment/priceAdjustmentApiTypes';
import CustomButton from '../../components/CustomButton';
import RowActionsMenu from '../../components/RowActionsMenu';
import CodeActionsCell from './components/CodeActionsCell';
import CustomModal from '../common/CustomModal';
import CreateCodeModal, { GoodwillDraft } from './components/CreateCodeModal';
import EditCodeModal from './components/EditCodeModal';
import BannerFormModal from './components/BannerFormModal';
import VoucherStatementPanel from './components/VoucherStatementPanel';
import PrepaidVouchersTab from './components/PrepaidVouchersTab';
import CreateCampaignModal from './components/CreateCampaignModal';
import CampaignResultModal from './components/CampaignResultModal';
import CampaignsTable from './components/CampaignsTable';
import {
    useDeleteBannerMutation,
    useGetBannersQuery,
} from '../../api/banner/bannerApiSlice';
import { AdminBanner } from '../../api/banner/bannerApiTypes';
import { useTenantCapabilities } from '../../hooks/useTenantCapabilities';
import { useSelector } from 'react-redux';
import { makeHasPermissionSelector, tenantSelector } from '../../selector/authenticationSelector';
import Permission from '../../common/permissions';
import { LEGACY_TENANT_SLUGS } from '../../api/types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { makeIssueReportDetailsUrl, paths } from '../../routes';
import { cn } from '../../utils/utils';
import { STATION_TIME_ZONE } from '../../utils/stationTime';
import { onRowClick } from '../../utils/rowClick';

type TabKey = 'prepaid' | 'promotional' | 'goodwill' | 'priceAdjustments' | 'banners';

// Goodwill go-live (#1189): the `code_type` + goodwill columns (#1335/#1349) and
// the BE goodwill-create handler (POST /admin/voucher with codeType +
// sourceReference) are now in place, so the Goodwill tab + create action are live.
const GOODWILL_CODES_ENABLED = true;

const headerCellClass = 'text-left px-4 py-2.5 text-foreground/60 font-medium';
const cellClass = 'px-4 py-2.5 text-foreground/80';

// The code tabs a tenant gets: prepaid vouchers are a SUP concept (balance +
// redemption ledger) and only show for SUP tenants — the tab used to leak to
// every charging tenant, incl. room-only ones (innodom review 1.1/7.1).
const buildCodeTabs = (
    t: (key: string) => string,
    hasPrepaidVouchers: boolean,
): SegmentedTabItem<TabKey>[] => [
    ...(hasPrepaidVouchers
        ? [{ key: 'prepaid' as TabKey, label: t('vouchers_codes.tabs.prepaid') }]
        : []),
    { key: 'promotional', label: t('vouchers_codes.tabs.promotional') },
    { key: 'goodwill', label: t('vouchers_codes.tabs.goodwill') },
    { key: 'priceAdjustments', label: t('vouchers_codes.tabs.price_adjustments') },
];

const VouchersCodesPage = () => {
    const { t } = useTranslation();
    // Banners live on the operational `tenant` row; legacy tenants have no such
    // row (their /admin/banners 500s), so the banner surface is self-service only.
    const selectedTenant = useSelector(tenantSelector);
    // The history under a code (statement, who issued it) is its own permission
    // (#3261); its endpoint answers 403 without it.
    const canViewHistory = useSelector(
        makeHasPermissionSelector(Permission.viewVoucherHistory()),
    );
    // Writes have their own strings (manage:vouchers / manage:banners); the
    // routes answer 403 without them.
    const canManageVouchers = useSelector(
        makeHasPermissionSelector(Permission.manageVouchers()),
    );
    const canManageBanners = useSelector(
        makeHasPermissionSelector(Permission.manageBanners()),
    );
    const isSelfServiceTenant =
        !!selectedTenant && !LEGACY_TENANT_SLUGS.has(selectedTenant);
    const [tab, setTab] = useState<TabKey>('prepaid');
    const [createOpen, setCreateOpen] = useState(false);
    // Promo-code campaign create (#2249) and what it produced.
    const [campaignCreateOpen, setCampaignCreateOpen] = useState(false);
    const [campaignResult, setCampaignResult] = useState<CreateCampaignResult | null>(null);
    const [goodwillCreateOpen, setGoodwillCreateOpen] = useState(false);
    // Goodwill form values to restore — set when the user left to file a
    // problem report and came back (see `resumeState` below). Held in state so
    // the object identity is stable; the modal applies it on identity change.
    const [goodwillResumeDraft, setGoodwillResumeDraft] = useState<GoodwillDraft | undefined>();
    const [bannerModalOpen, setBannerModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState<AdminBanner | null>(null);
    // Prepaid row whose redemption history is expanded (#1000 Step 7B).
    const [expandedVoucherId, setExpandedVoucherId] = useState<string | null>(null);
    // Edit / cancel / delete of a promo or goodwill code (#1189). Delete (hard,
    // master-only) and edit require an unused code; cancel (soft-disable) works
    // on used codes too.
    const isMaster = useIsMaster();
    const [editingVoucher, setEditingVoucher] = useState<AdminVoucher | null>(null);
    const [deletingVoucher, setDeletingVoucher] = useState<AdminVoucher | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [cancellingVoucher, setCancellingVoucher] = useState<AdminVoucher | null>(null);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [deleteCode, { isLoading: isDeleting }] = useDeleteCodeMutation();
    const [cancelCode, { isLoading: isCancelling }] = useCancelCodeMutation();
    const handleCancelRequest = (v: AdminVoucher) => {
        setCancelError(null);
        setCancellingVoucher(v);
    };
    const handleDeleteRequest = (v: AdminVoucher) => {
        setDeleteError(null);
        setDeletingVoucher(v);
    };
    // Refetch on mount: a code is REDEEMED in the end-user app, so nothing in
    // this app can ever invalidate the cached list. Without this the redemption
    // count silently showed a stale value — observed on production 2026-08-06,
    // a code read 0/5 immediately after a booking had used it and only
    // corrected on a full browser reload. A counter that lags without saying so
    // is worse than a slow one on the screen an operator checks a code from.
    const { data: vouchers, isLoading, isError } = useGetVouchersQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const {
        data: banners,
        isLoading: bannersLoading,
        isError: bannersError,
    } = useGetBannersQuery(undefined, { skip: !isSelfServiceTenant });
    const [deleteBanner] = useDeleteBannerMutation();
    const { usesPaymentCodes, services } = useTenantCapabilities();
    // Prepaid vouchers are a SUP concept (balance + redemption ledger); the
    // tab leaked to every charging tenant, incl. room-only ones (innodom
    // review 1.1/7.1).
    const hasPrepaidVouchers = services.includes('sup');
    // Bookable groups, to resolve a code's `groupId` → group name for the
    // "Applies to" column. Same source the create-code group select uses.
    const { data: groups } = useGetAdminGroupsQuery(undefined, { skip: !usesPaymentCodes });
    // Manual price adjustments made while editing reservations (own tab).
    const {
        data: priceAdjustments,
        isLoading: priceAdjustmentsLoading,
        isError: priceAdjustmentsError,
    } = useGetPriceAdjustmentsQuery(undefined, { skip: !usesPaymentCodes });

    const naLabel = t('common.not_applicable');
    // A price adjustment is always made by a tenant staff member; when we can't
    // resolve which one (legacy tenants have no app_user, old rows predate actor
    // capture) we say "team member" rather than n/a — the creator isn't absent,
    // just unattributed.
    const teamMemberLabel = t('vouchers_codes.team_member');

    const groupNameById = useMemo(() => {
        const map = new Map<string, string>();
        (groups ?? []).forEach((g) => map.set(g.id, g.name ?? g.id.slice(0, 8)));
        return map;
    }, [groups]);
    // Station names, for a code bound to a location but no group (older rows):
    // it applies at that station only, so "All objects" would be false.
    const locationNameById = useMemo(() => {
        const map = new Map<string, string>();
        (groups ?? []).forEach((g) => {
            if (g.locationId && g.locationName) map.set(g.locationId, g.locationName);
        });
        return map;
    }, [groups]);
    // Which group a promo / goodwill code applies to. A code with no group is a
    // tenant-wide promotion covering every object — say so rather than n/a,
    // which would read as "unknown" for a deliberate choice. n/a stays for a
    // code whose group exists but hasn't resolved yet (list still loading).
    const formatScope = (v: VoucherScope) => {
        if (v.groupId) return groupNameById.get(v.groupId) ?? naLabel;
        if (v.locationId) return locationNameById.get(v.locationId) ?? naLabel;
        return t('vouchers_codes.all_objects');
    };

    const prepaid = useMemo(
        () => (vouchers ?? []).filter((v) => v.codeType === 'PREPAID_VOUCHER'),
        [vouchers],
    );
    // Campaign codes (#2249) are listed under their campaign, not one by one.
    const promotional = useMemo(
        () => (vouchers ?? []).filter((v) => v.codeType === 'PROMOTIONAL' && !v.campaignId),
        [vouchers],
    );
    const goodwill = useMemo(
        () => (vouchers ?? []).filter((v) => v.codeType === 'GOODWILL_COMPENSATION'),
        [vouchers],
    );

    // Money with its currency — amounts without a currency are ambiguous.
    // Falls back to the plain number when no currency is known (promo codes
    // carry no payment, so no currency), or to "<value> <code>" if Intl
    // rejects the code.
    const formatMoney = (value: number | null, currency: string | null) => {
        if (value == null) return naLabel;
        if (currency) {
            try {
                return new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency,
                }).format(value);
            } catch {
                return `${value.toFixed(2)} ${currency}`;
            }
        }
        return value.toFixed(2);
    };

    const formatDate = (iso: string | null) =>
        iso ? new Date(iso).toLocaleDateString(undefined, { timeZone: STATION_TIME_ZONE }) : naLabel;

    const formatValue = (
        v: Pick<AdminVoucher, 'discountValue' | 'discountIsPercentage' | 'currency'>,
    ) => {
        if (v.discountValue == null) return naLabel;
        return v.discountIsPercentage
            ? `${v.discountValue}%`
            : formatMoney(v.discountValue, v.currency);
    };

    // Signed money for a price adjustment: '+' for a surcharge, '-' for a
    // discount, so positive vs. negative is unambiguous at a glance.
    const formatAdjustment = (a: PriceAdjustment) => {
        if (a.adjustmentAmount == null) return naLabel;
        const money = formatMoney(Math.abs(a.adjustmentAmount), a.currency);
        if (a.adjustmentAmount === 0) return money;
        return `${a.adjustmentAmount > 0 ? '+' : '-'}${money}`;
    };

    // Edit / delete actions for promo + goodwill rows (#1189). Only unused codes
    // can be changed; a redeemed code (usedCounter > 0) is frozen, so no actions.
    const toggleExpanded = (id: string) =>
        setExpandedVoucherId((cur) => (cur === id ? null : id));

    // The expandable history row under a promo or goodwill code: who created it,
    // why, and its redemptions (#1189, #3332 D4). Prepaid rows have their own
    // in PrepaidVouchersTab, with the same panel.
    const redemptionDetailRow = (v: AdminVoucher, colSpan: number) =>
        canViewHistory && expandedVoucherId === v.id ? (
            <tr className={'border-b border-foreground/5 last:border-b-0'}>
                <td colSpan={colSpan} className={'bg-foreground/[0.02]'}>
                    <VoucherStatementPanel
                        voucherId={v.id}
                        currency={v.currency}
                        naLabel={naLabel}
                        formatMoney={formatMoney}
                        formatDate={formatDate}
                    />
                </td>
            </tr>
        ) : null;

    const handleConfirmDelete = async () => {
        if (!deletingVoucher) return;
        setDeleteError(null);
        try {
            await deleteCode(deletingVoucher.id).unwrap();
            setDeletingVoucher(null);
        } catch (err) {
            const message =
                typeof err === 'object' &&
                err !== null &&
                'data' in err &&
                typeof (err as { data?: { message?: string } }).data?.message === 'string'
                    ? (err as { data: { message: string } }).data.message
                    : t('vouchers_codes.delete.error');
            setDeleteError(message);
        }
    };

    const handleConfirmCancel = async () => {
        if (!cancellingVoucher) return;
        setCancelError(null);
        try {
            await cancelCode(cancellingVoucher.id).unwrap();
            setCancellingVoucher(null);
        } catch (err) {
            const message =
                typeof err === 'object' &&
                err !== null &&
                'data' in err &&
                typeof (err as { data?: { message?: string } }).data?.message === 'string'
                    ? (err as { data: { message: string } }).data.message
                    : t('vouchers_codes.cancel_code.error');
            setCancelError(message);
        }
    };

    const codeTabs: SegmentedTabItem<TabKey>[] = buildCodeTabs(t, hasPrepaidVouchers);
    const bannersTab: SegmentedTabItem<TabKey> = {
        key: 'banners',
        label: t('vouchers_codes.tabs.banners'),
    };

    // Code tabs (prepaid / promotional / goodwill) only apply when the tenant
    // takes payment. Banners are self-service-only (see above), so the banners
    // tab shows for self-service tenants only. Active tab = the current one if
    // still visible, else the first visible tab.
    const visibleTabs: SegmentedTabItem<TabKey>[] = [
        ...(usesPaymentCodes ? codeTabs : []),
        ...(isSelfServiceTenant ? [bannersTab] : []),
    ];
    const activeTab: TabKey =
        visibleTabs.find((tabItem) => tabItem.key === tab)?.key ??
        visibleTabs[0]?.key ??
        'prepaid';

    // The banner's name in its row menu's label: type and position, plus a
    // short id where two banners share both (the label must tell rows apart).
    const bannerRowName = (banner: AdminBanner) => {
        const type = t(`banners.types.${banner.type ?? 'explainer'}`);
        const order = banner.order ?? 0;
        const twin = (banners ?? []).some((other) => other.id !== banner.id
            && (other.order ?? 0) === order && (other.type ?? 'explainer') === (banner.type ?? 'explainer'));
        return twin
            ? t('banners.row_label_with_id', { type, order, id: banner.id.slice(0, 8) })
            : t('banners.row_label', { type, order });
    };

    const openCreateBanner = () => {
        setEditingBanner(null);
        setBannerModalOpen(true);
    };

    const openEditBanner = (banner: AdminBanner) => {
        setEditingBanner(banner);
        setBannerModalOpen(true);
    };

    const handleDeleteBanner = async (id: string) => {
        try {
            await deleteBanner(id).unwrap();
        } catch {
            // Surfaced via the list's error state on refetch; no extra UI here.
        }
    };

    // --- Goodwill ⇄ problem report round trip ---------------------------
    // A goodwill code often gets decided before the problem report exists, so
    // the picker can send the user to the report form and bring them straight
    // back here with the new report linked.
    const navigate = useNavigate();
    const location = useLocation();
    const canCreateIssueReports = useSelector(
        makeHasPermissionSelector(Permission.createIssueReports()),
    );
    const resumeState = location.state as {
        openGoodwillCreate?: boolean;
        goodwillDraft?: GoodwillDraft;
        createdIssueReportId?: string | null;
    } | null;

    useEffect(() => {
        if (!resumeState?.openGoodwillCreate) return;
        const draft = resumeState.goodwillDraft;
        setTab('goodwill');
        if (draft) {
            // Restore the form as it was, with the report they just filed
            // linked. A failed create comes back without an id — keep whatever
            // was selected before rather than clearing it.
            setGoodwillResumeDraft({
                ...draft,
                sourceReference: resumeState.createdIssueReportId ?? draft.sourceReference,
            });
        }
        setGoodwillCreateOpen(true);
        // Consume the navigation state so a plain reload (or a later back
        // navigation) doesn't reopen the modal again. Keep the query string —
        // dropping it would silently discard any filters in the URL.
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeState?.openGoodwillCreate, resumeState?.createdIssueReportId]);

    const openGoodwillCreate = () => {
        setGoodwillResumeDraft(undefined);
        setGoodwillCreateOpen(true);
    };

    const goCreateProblemReport = (draft: GoodwillDraft) => {
        setGoodwillCreateOpen(false);
        navigate(paths.addIssueReports, {
            state: {
                returnTo: paths.vouchersCodes,
                returnState: { openGoodwillCreate: true, goodwillDraft: draft },
            },
        });
    };

    const emptyRow = (colSpan: number) => (
        <tr>
            <td colSpan={colSpan} className={'px-4 py-3'}>
                <span className={'text-sm text-foreground/40 italic'}>
                    {t('vouchers_codes.empty')}
                </span>
            </td>
        </tr>
    );

    // Shared list-state renderers, reused across the banners / price-adjustments /
    // code tabs so the spinner and error markup live in one place.
    const loadingSpinner = (
        <div className={'flex justify-center py-16'}>
            <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
        </div>
    );
    const loadErrorMessage = (messageKey: string) => (
        <p className={'mt-6 text-sm text-foreground/60'}>{t(messageKey)}</p>
    );

    return (
        <div className={'px-4 sm:px-6 py-6 max-w-7xl'}>
            <PageHeader>
                <PageHeadline>{t('vouchers_codes.title')}</PageHeadline>
                {activeTab === 'promotional' && canManageVouchers && (
                    <div className={'flex gap-2'}>
                        <CustomButton
                            title={t('vouchers_codes.campaign.button')}
                            isFilled={false}
                            onClick={() => setCampaignCreateOpen(true)}
                        />
                        <CustomButton
                            title={t('vouchers_codes.create_promo.button')}
                            onClick={() => setCreateOpen(true)}
                        />
                    </div>
                )}
                {activeTab === 'goodwill' && GOODWILL_CODES_ENABLED && canManageVouchers && (
                    <CustomButton
                        title={t('vouchers_codes.create_goodwill.button')}
                        onClick={openGoodwillCreate}
                    />
                )}
                {activeTab === 'banners' && canManageBanners && (
                    <CustomButton
                        title={t('banners.create_button')}
                        onClick={openCreateBanner}
                    />
                )}
            </PageHeader>

            {visibleTabs.length > 1 && (
                <SegmentedTabs items={visibleTabs} value={activeTab} onChange={setTab} />
            )}

            {activeTab === 'banners' ? (
                bannersLoading ? (
                    loadingSpinner
                ) : bannersError ? (
                    loadErrorMessage('banners.load_error')
                ) : (
                    <div className={'mt-6 bg-text-contrast rounded-[5px] shadow-sm overflow-x-auto'}>
                        <table className={'w-full text-sm'}>
                            <thead>
                                <tr className={'border-b border-foreground/10'}>
                                    <th className={headerCellClass}>{t('banners.columns.preview')}</th>
                                    <th className={headerCellClass}>{t('banners.columns.languages')}</th>
                                    <th className={headerCellClass}>{t('banners.columns.type')}</th>
                                    <th className={headerCellClass}>{t('banners.columns.target')}</th>
                                    <th className={headerCellClass}>{t('banners.columns.promo_code')}</th>
                                    <th className={headerCellClass}>{t('banners.columns.schedule')}</th>
                                    <th className={headerCellClass}>{t('banners.columns.order')}</th>
                                    <th className={headerCellClass}>{t('banners.columns.status')}</th>
                                    <th className={headerCellClass}>{t('banners.columns.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(banners ?? []).map((b) => (
                                    <tr key={b.id} className={'border-b border-foreground/5 last:border-b-0'}>
                                        <td className={cellClass}>
                                            <img
                                                src={b.images.de ?? b.images.en}
                                                alt={''}
                                                className={'h-10 w-24 object-cover rounded'}
                                            />
                                        </td>
                                        <td className={cellClass}>
                                            {[
                                                b.images.de ? 'DE' : null,
                                                b.images.en ? 'EN' : null,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ') || naLabel}
                                        </td>
                                        <td className={cellClass}>{t(`banners.types.${b.type ?? 'explainer'}`)}</td>
                                        <td className={cellClass}>{b.href ?? naLabel}</td>
                                        <td className={cellClass}>{b.voucherCode ?? naLabel}</td>
                                        <td className={cellClass}>
                                            {`${formatDate(b.startsAt ?? null)} – ${formatDate(b.endsAt ?? null)}`}
                                        </td>
                                        <td className={cellClass}>{b.order}</td>
                                        <td className={cellClass}>
                                            {/* A status, not a switch: a click used to take a live
                                                banner off the app at once. Active is changed in the
                                                banner's edit dialog, which saves on Speichern. */}
                                            <span className={b.active ? 'font-medium text-foreground' : 'text-foreground/40'}>
                                                {b.active ? t('banners.status.active') : t('banners.status.inactive')}
                                            </span>
                                        </td>
                                        <td className={cellClass}>
                                            {/* Both change something, so both sit in the
                                                row's menu, Löschen last (design-decisions.md ›
                                                Zeilenaktionen in Tabellen). */}
                                            <RowActionsMenu
                                                rowName={bannerRowName(b)}
                                                items={[{
                                                    id: 'edit',
                                                    label: t('banners.edit'),
                                                    onClick: () => openEditBanner(b),
                                                    hidden: !canManageBanners,
                                                }]}
                                                destructiveItems={[{
                                                    id: 'delete',
                                                    label: t('banners.delete'),
                                                    onClick: () => handleDeleteBanner(b.id),
                                                    hidden: !canManageBanners,
                                                }]}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                {(banners ?? []).length === 0 && emptyRow(9)}
                            </tbody>
                        </table>
                    </div>
                )
            ) : activeTab === 'priceAdjustments' ? (
                priceAdjustmentsLoading ? (
                    loadingSpinner
                ) : priceAdjustmentsError ? (
                    loadErrorMessage('vouchers_codes.load_error')
                ) : (
                    <div className={'mt-6 bg-text-contrast rounded-[5px] shadow-sm overflow-x-auto'}>
                        <table className={'w-full text-sm'}>
                            <thead>
                                <tr className={'border-b border-foreground/10'}>
                                    <th className={headerCellClass}>{t('vouchers_codes.columns.booking_number')}</th>
                                    <th className={headerCellClass}>{t('vouchers_codes.columns.customer')}</th>
                                    <th className={headerCellClass}>{t('vouchers_codes.columns.object')}</th>
                                    <th className={headerCellClass}>{t('vouchers_codes.columns.period')}</th>
                                    <th className={headerCellClass}>{t('vouchers_codes.columns.adjustment')}</th>
                                    <th className={headerCellClass}>{t('vouchers_codes.columns.comment')}</th>
                                    <th className={headerCellClass}>{t('vouchers_codes.columns.created_by')}</th>
                                    <th className={headerCellClass}>{t('vouchers_codes.columns.adjusted_on')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(priceAdjustments ?? []).map((a) => (
                                    <tr key={a.id} className={'border-b border-foreground/5 last:border-b-0'}>
                                        <td className={cellClass}>{a.bookingNumber ?? naLabel}</td>
                                        <td className={cellClass}>{a.customerName ?? a.customerEmail ?? naLabel}</td>
                                        <td className={cellClass}>{a.objectName ?? naLabel}</td>
                                        <td className={cellClass}>{`${formatDate(a.periodStart)} – ${formatDate(a.periodEnd)}`}</td>
                                        <td className={cellClass}>{formatAdjustment(a)}</td>
                                        <td className={cellClass}>{a.reason ?? naLabel}</td>
                                        <td className={cellClass}>{a.adjustedByName ?? a.adjustedByEmail ?? teamMemberLabel}</td>
                                        <td className={cellClass}>{formatDate(a.adjustedAt)}</td>
                                    </tr>
                                ))}
                                {(priceAdjustments ?? []).length === 0 && emptyRow(8)}
                            </tbody>
                        </table>
                    </div>
                )
            ) : isLoading ? (
                loadingSpinner
            ) : isError ? (
                loadErrorMessage('vouchers_codes.load_error')
            ) : (
                <div className={'mt-6'}>
                    {activeTab === 'prepaid' && (
                        <PrepaidVouchersTab
                            vouchers={prepaid}
                            canViewHistory={canViewHistory}
                            naLabel={naLabel}
                            formatMoney={formatMoney}
                            formatDate={formatDate}
                        />
                    )}

                    {activeTab === 'promotional' && (
                        <CampaignsTable
                            vouchers={vouchers ?? []}
                            formatScope={formatScope}
                            formatValue={formatValue}
                            formatDate={formatDate}
                            naLabel={naLabel}
                        />
                    )}

                    {activeTab === 'promotional' && (
                        <div className={'bg-text-contrast rounded-[5px] shadow-sm overflow-x-auto'}>
                            <table className={'w-full text-sm'}>
                                <thead>
                                    <tr className={'border-b border-foreground/10'}>
                                        <th className={headerCellClass}>{t('vouchers_codes.columns.code')}</th>
                                        <th className={headerCellClass}>{t('vouchers_codes.columns.applies_to')}</th>
                                        <th className={headerCellClass}>{t('vouchers_codes.columns.value')}</th>
                                        <th className={headerCellClass}>{t('vouchers_codes.columns.used')}</th>
                                        <th className={headerCellClass}>{t('vouchers_codes.columns.expiry')}</th>
                                        <th className={headerCellClass}>{t('vouchers_codes.columns.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {promotional.map((v) => (
                                        <Fragment key={v.id}>
                                            <tr
                                                className={cn(
                                                    'border-b border-foreground/5 last:border-b-0'
                                                + ' cursor-pointer hover:bg-foreground/[0.02]',
                                                    v.archivedAt && 'opacity-60',
                                                )}
                                                onClick={onRowClick(() => toggleExpanded(v.id))}
                                            >
                                                <td className={cellClass}>{v.code ?? naLabel}</td>
                                                <td className={cellClass}>{formatScope(v)}</td>
                                                <td className={cellClass}>{formatValue(v)}</td>
                                                <td className={cellClass}>{`${v.usedCounter} / ${v.maxLimit}`}</td>
                                                <td className={cellClass}>{formatDate(v.validUntilDate)}</td>
                                                <CodeActionsCell
                                                    voucher={v}
                                                    isMaster={isMaster}
                                                    canManage={canManageVouchers}
                                                    onCancel={handleCancelRequest}
                                                    onEdit={setEditingVoucher}
                                                    onDelete={handleDeleteRequest}
                                                />
                                            </tr>
                                            {redemptionDetailRow(v, 6)}
                                        </Fragment>
                                    ))}
                                    {promotional.length === 0 && emptyRow(6)}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === 'goodwill' &&
                        (GOODWILL_CODES_ENABLED ? (
                            <div className={'bg-text-contrast rounded-[5px] shadow-sm overflow-x-auto'}>
                                <table className={'w-full text-sm'}>
                                    <thead>
                                        <tr className={'border-b border-foreground/10'}>
                                            <th className={headerCellClass}>{t('vouchers_codes.columns.code')}</th>
                                            <th className={headerCellClass}>{t('vouchers_codes.columns.applies_to')}</th>
                                            <th className={headerCellClass}>{t('vouchers_codes.columns.customer')}</th>
                                            <th className={headerCellClass}>{t('vouchers_codes.columns.issue_report')}</th>
                                            <th className={headerCellClass}>{t('vouchers_codes.columns.value')}</th>
                                            <th className={headerCellClass}>{t('vouchers_codes.columns.used')}</th>
                                            <th className={headerCellClass}>{t('vouchers_codes.columns.expiry')}</th>
                                            <th className={headerCellClass}>{t('vouchers_codes.columns.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {goodwill.map((v) => (
                                            <Fragment key={v.id}>
                                                <tr
                                                    className={cn(
                                                        'border-b border-foreground/5 last:border-b-0'
                                                + ' cursor-pointer hover:bg-foreground/[0.02]',
                                                        v.archivedAt && 'opacity-60',
                                                    )}
                                                    onClick={onRowClick(() => toggleExpanded(v.id))}
                                                >
                                                    <td className={cellClass}>{v.code ?? naLabel}</td>
                                                    <td className={cellClass}>{formatScope(v)}</td>
                                                    {/* Derived contact first; the admin's stated recipient from
                                                        the create dialog as fallback (e.g. reports without an
                                                        order to derive the customer from). */}
                                                    <td className={cellClass}>{v.customerName ?? v.customerEmail ?? v.customerReference ?? naLabel}</td>
                                                    <td className={cellClass}>
                                                        {v.sourceReportId ? (
                                                            <Link
                                                                to={makeIssueReportDetailsUrl(v.sourceReportId)}
                                                                className={'text-primary hover:underline'}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {v.sourceReportBookingNumber ?? t('vouchers_codes.view_report')}
                                                            </Link>
                                                        ) : (
                                                            naLabel
                                                        )}
                                                    </td>
                                                    <td className={cellClass}>{formatValue(v)}</td>
                                                    <td className={cellClass}>{`${v.usedCounter} / ${v.maxLimit}`}</td>
                                                    <td className={cellClass}>{formatDate(v.validUntilDate)}</td>
                                                    <CodeActionsCell
                                                        voucher={v}
                                                        isMaster={isMaster}
                                                        canManage={canManageVouchers}
                                                        onCancel={handleCancelRequest}
                                                        onEdit={setEditingVoucher}
                                                        onDelete={handleDeleteRequest}
                                                    />
                                                </tr>
                                                {redemptionDetailRow(v, 8)}
                                            </Fragment>
                                        ))}
                                        {goodwill.length === 0 && emptyRow(8)}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className={'text-sm text-foreground/60'}>
                                {t('vouchers_codes.goodwill_placeholder')}
                            </p>
                        ))}
                </div>
            )}

            <CreateCodeModal open={createOpen} onClose={() => setCreateOpen(false)} />
            <CreateCampaignModal
                open={campaignCreateOpen}
                onClose={() => setCampaignCreateOpen(false)}
                onCreated={setCampaignResult}
            />
            <CampaignResultModal
                result={campaignResult}
                onClose={() => setCampaignResult(null)}
            />
            <CreateCodeModal
                open={goodwillCreateOpen}
                codeType={'GOODWILL_COMPENSATION'}
                resumeDraft={goodwillResumeDraft}
                // Same gate as the /issue-reports/add route — without the
                // permission the action would only lead to "unauthorized".
                onCreateProblemReport={
                    canCreateIssueReports ? goCreateProblemReport : undefined
                }
                onClose={() => setGoodwillCreateOpen(false)}
            />
            <BannerFormModal
                open={bannerModalOpen}
                banner={editingBanner}
                onClose={() => setBannerModalOpen(false)}
            />
            <EditCodeModal
                open={!!editingVoucher}
                voucher={editingVoucher}
                onClose={() => setEditingVoucher(null)}
            />
            <CustomModal
                open={!!deletingVoucher}
                title={t('vouchers_codes.delete.title')}
                onClose={() => setDeletingVoucher(null)}
                closeButton={
                    <CustomButton
                        title={t('common.cancel')}
                        isFilled={false}
                        onClick={() => setDeletingVoucher(null)}
                    />
                }
                saveButton={
                    <CustomButton
                        title={t('common.delete')}
                        loading={isDeleting}
                        disabled={isDeleting}
                        onClick={handleConfirmDelete}
                    />
                }
            >
                <div className={'flex flex-col gap-3'}>
                    <p className={'text-sm text-foreground/80'}>
                        {t('vouchers_codes.delete.message', {
                            code: deletingVoucher?.code ?? '',
                        })}
                    </p>
                    {deleteError && (
                        <p className={'text-sm text-red-500'}>{deleteError}</p>
                    )}
                </div>
            </CustomModal>
            <CustomModal
                open={!!cancellingVoucher}
                title={t('vouchers_codes.cancel_code.title')}
                onClose={() => setCancellingVoucher(null)}
                closeButton={
                    <CustomButton
                        title={t('vouchers_codes.cancel_code.keep')}
                        isFilled={false}
                        onClick={() => setCancellingVoucher(null)}
                    />
                }
                saveButton={
                    <CustomButton
                        title={t('vouchers_codes.cancel_code.confirm')}
                        loading={isCancelling}
                        disabled={isCancelling}
                        onClick={handleConfirmCancel}
                    />
                }
            >
                <div className={'flex flex-col gap-3'}>
                    <p className={'text-sm text-foreground/80'}>
                        {t('vouchers_codes.cancel_code.message', {
                            code: cancellingVoucher?.code ?? '',
                        })}
                    </p>
                    {cancelError && (
                        <p className={'text-sm text-red-500'}>{cancelError}</p>
                    )}
                </div>
            </CustomModal>
        </div>
    );
};

export default VouchersCodesPage;
